import { Transform, Type } from 'class-transformer';
import { ArrayMinSize, ArrayNotEmpty, IsBoolean, IsIn, IsNotEmpty, IsNumber, IsOptional, IsUUID, Min, ValidateIf, ValidateNested } from 'class-validator';
import { IPack, LoyaltyEarningMode, LoyaltyType, OrderMode } from '../interfaces';
import { TypeORMRelationId } from './utils.dto';

export class LoyaltyDTO {
  @IsNotEmpty({ message: 'Le type de fidélité est requis.' })
  @IsIn([LoyaltyType.LEVEL], { message: 'Le type de fidélité est incorrect.' })
  type: LoyaltyType;

  @IsOptional()
  @Transform((value) => typeof value === 'string' ? +value : value)
  @IsNumber()
  @Min(0, { message: 'Le nombre de point maximal doit être positif.' })
  maxPoints: number;

  @IsOptional()
  @Transform((value) => typeof value === 'string' ? +value : value)
  @IsNumber()
  @Min(0, { message: 'Le montant minimum doit être positif.' })
  minPrice: number;

  @IsOptional()
  @Transform((value) => typeof value === 'string' ? +value : value)
  @IsNumber()
  @Min(0, { message: 'Le nombre de jours avant la remise à 0 des points doit être positif.' })
  daysWithoutOrderUntilReset: number;

  @IsOptional()
  @Transform((value) => typeof value === 'string' ? +value : value)
  @IsNumber()
  @Min(0, { message: 'Le montant minimum doit être positif.' })
  exchangeRateForOneEuro: number;

  @IsOptional()
  @IsIn(Object.values(LoyaltyEarningMode), { message: 'Le mode de gain est incorrect.' })
  earningMode: LoyaltyEarningMode;

  @IsOptional()
  @ArrayMinSize(1, { message: 'Au moins un type de vente est requis' })
  @IsIn(Object.values(OrderMode), {
    message: 'Le type de vente est incorrect',
    each: true
  })
  orderModes: OrderMode[];

  @IsOptional()
  @IsBoolean()
  usableOnlyOnNextOrder: boolean;

  @IsOptional()
  @IsBoolean()
  byShop: boolean;

  @IsOptional()
  description: string;

  // '******************************************************************************'
  // '*                                  LOYALTY BY LEVEL                          *'
  // '******************************************************************************'

  @ValidateIf((dto: LoyaltyDTO) => dto.type === LoyaltyType.LEVEL)
  @ArrayNotEmpty({ message: 'Au moins un palier est requis. ' })
  @ArrayMinSize(1, { message: 'Au moins un palier est requis. ' })
  @ValidateNested()
  @Type(() => LoyaltyLevelDTO)
  levels: LoyaltyLevelDTO[];

  // '******************************************************************************'
  // '*                                  LOYALTY BY DISCOUNT                       *'
  // '******************************************************************************'

  @ValidateIf((dto: LoyaltyDTO) => dto.type === LoyaltyType.DISCOUNT)
  @IsNotEmpty({ message: 'Le nombre de point pour appliquer la réduction est requis.' })
  @Transform((value) => typeof value === 'string' ? +value : value)
  @IsNumber()
  @Min(0, { message: 'Le nombre de point doit être positif.' })
  loyaltyPointsNeeded: number;

  @ValidateIf((dto: LoyaltyDTO) => dto.type === LoyaltyType.DISCOUNT)
  @IsNotEmpty({ message: 'Le montant de la réduction requis.' })
  @Transform((value) => typeof value === 'string' ? +value : value)
  @IsNumber()
  @Min(0, { message: 'Le montant de la réduction doit être positif.' })
  discount: number;

  @ValidateIf((dto: LoyaltyDTO) => dto.type === LoyaltyType.DISCOUNT)
  @IsOptional()
  @IsBoolean()
  automatic: boolean;
}

class LoyaltyLevelDTO {

  @IsNotEmpty({ message: 'Le nombre de point requis pour le palier est requis.' })
  @Transform((value) => typeof value === 'string' ? +value : value)
  @IsNumber()
  @Min(0, { message: 'Le nombre de point requis pour le palier doit être positif.' })
  loyaltyPointsNeeded: number;

  @IsNotEmpty({ message: 'Le menu est requis' })
  @Type(() => TypeORMRelationId)
  pack: Pick<IPack, '_id'>;
}
